import { useEffect, useState } from 'react';
import { receivablesApi, type ReceivablesData } from '../lib/data.js';
import { formatOre } from '../lib/format.js';
import { PageHeader, Card, StatCard, DeleteButton, TextField, OreField } from '../components/ui.js';
import { SectionBar, Tr, Th, Td, TdNum } from '../components/table.js';

/**
 * Receivables / Tilgodehavender (sheet "Lenders"). A flat list of money or items lent to
 * others, with a total that can be read as a net-worth asset. Simple inline-edit CRUD table.
 */
export function Receivables(): JSX.Element {
  const [data, setData] = useState<ReceivablesData | null>(null);
  const reload = () => receivablesApi.load().then(setData);
  useEffect(() => {
    void reload();
  }, []);

  if (!data) return <PageHeader title="Receivables" subtitle="Loading…" />;

  const total = data.receivables.reduce((s, r) => s + r.amount_ore, 0);

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Receivables"
        subtitle={
          <>
            Money and things you have lent out. The total can be read as an asset in your net worth.
            Reproduces the spreadsheet's <span className="font-mono">Lenders</span> tab.
          </>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <StatCard label="Total owed to you" value={formatOre(total)} tone="success" hint="Sum of all entries" />
        <StatCard label="Number of entries" value={String(data.receivables.length)} />
      </div>

      <Card className="overflow-hidden">
        <SectionBar
          title="Entries"
          onAdd={() =>
            receivablesApi.add({ debtor: 'New debtor', item: '', amount_ore: 0 }).then(reload)
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-hv-sm">
            <thead>
              <Tr head>
                <Th>Debtor</Th>
                <Th>What</Th>
                <Th right>Amount</Th>
                <Th />
              </Tr>
            </thead>
            <tbody>
              {data.receivables.map((r) => (
                <Tr key={r.id}>
                  <Td>
                    <TextField value={r.debtor} onCommit={(v) => receivablesApi.update(r.id, { debtor: v }).then(reload)} />
                  </Td>
                  <Td>
                    <TextField
                      value={r.item ?? ''}
                      placeholder="e.g. loan, book"
                      onCommit={(v) => receivablesApi.update(r.id, { item: v }).then(reload)}
                    />
                  </Td>
                  <Td right>
                    <OreField value={r.amount_ore} onCommit={(v) => receivablesApi.update(r.id, { amount_ore: v }).then(reload)} />
                  </Td>
                  <Td>
                    <DeleteButton onClick={() => receivablesApi.remove(r.id).then(reload)} />
                  </Td>
                </Tr>
              ))}
              {data.receivables.length === 0 && (
                <Tr>
                  <Td colSpan={4}>
                    <span className="text-hv-text-tertiary">No receivables yet.</span>
                  </Td>
                </Tr>
              )}
            </tbody>
            {data.receivables.length > 0 && (
              <tfoot>
                <Tr>
                  <Td>
                    <span className="font-medium text-hv-text">Total</span>
                  </Td>
                  <Td />
                  <TdNum strong>{formatOre(total)}</TdNum>
                  <Td />
                </Tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
    </div>
  );
}
